import { modelHelperModule } from './model-helper';
import { teamCandidateModelModule } from './team-candidate';
import * as PercentileUtils from '../utils/percentile-utils';
import * as DateUtils from '../utils/date-utils';
import { dateNullOrAfter, dateNullOrBefore } from '../utils/date-utils';
import { absoluteUrl, camelCase } from '../utils/string-utils';
import { defineValue } from '../utils/object-utils/object-utils';
import { getUser } from '../utils/account-utils/account-utils';
import { isDate } from '../utils/type-utils';
import { noop } from '../utils/function-utils';

const CLOSED_REASONS = {
  candidateSubmitted: 'submitted by candidate',
  timerRanOut: 'auto-submitted after timer ran out',
  teamDecided: 'closed before submission by team decision',
  teamForced: 'closed before submission by team',
  invitationExpired: 'closed by invitation expiration',
  assessmentArchived: 'closed by assessment archival',
};

export const RETRY_ACTION_RETAKE = 'retake';
export const RETRY_ACTION_REOPEN = 'reopen';

export const EDIT_MODE_READONLY = 'readonly';
export const EDIT_MODE_RESTRICTED = 'restricted';
export const EDIT_MODE_NORMAL = 'normal';
export const EDIT_MODE_LABELS = {
  [EDIT_MODE_NORMAL]: 'Normal',
  [EDIT_MODE_READONLY]: 'Read-Only',
  [EDIT_MODE_RESTRICTED]: 'Restricted',
};

export const assessmentResultModelModule = angular
  .module('Q.models.assessment-result', [
    modelHelperModule,
    teamCandidateModelModule,
  ])

  .factory('AssessmentResult', function (ModelHelper, DS, $injector, $log) {
    const currentUser = getUser.bind(null, $injector);

    // Don't require a hard dependency on $state, mock if missing
    const $state = ($injector.has('$state') && $injector.get('$state')) || {
      params: {},
      href: noop,
    };

    const AssessmentResult = ModelHelper.defineResource({
      name: 'AssessmentResult',
      endpoint: 'assessment_results',

      computed: {
        // enables overriding the Assessment redirectUrl with a custom one in the app URL
        //
        $redirectUrl: {
          get() {
            return $state.params.redirectUrl || this.redirectUrl;
          },
        },
      },

      methods: {
        canEdit() {
          return !this.accessLocked && !this.isClosed();
        },
        isInRetryAccessWindow() {
          return (
            !!this.retrySchedule &&
            this.currentTake &&
            dateNullOrBefore(this.retrySchedule.accessStartsAt) &&
            dateNullOrAfter(this.retrySchedule.accessEndsAt)
          );
        },
        isReopenScheduled() {
          return (
            this.isInRetryAccessWindow() &&
            this.retrySchedule.action === RETRY_ACTION_REOPEN
          );
        },
        isRetakeScheduled() {
          return (
            this.isInRetryAccessWindow() &&
            this.retrySchedule.action === RETRY_ACTION_RETAKE
          );
        },
        isReadOnly() {
          return this.isActive() && this.editMode === EDIT_MODE_READONLY;
        },
        isRestricted() {
          return (
            this.isActive() &&
            (this.isReadOnly() || this.editMode === EDIT_MODE_RESTRICTED)
          );
        },
        canUnsubmit() {
          return (
            this.isClosed() &&
            this.teamCandidate &&
            !this.teamCandidate.forgotten &&
            !this.teamCandidate.demoData
          );
        },
        submitStartedAt() {
          return this.submittingAt || this.submittedAt;
        },
        isInvited() {
          return this.state === 'invited';
        },
        isOpened() {
          return this.state === 'opened';
        },
        accessUnlockedByLabel() {
          return (
            (this.accessUnlockedBy && this.accessUnlockedBy.nameOrEmail) ||
            '(User Data Missing)'
          );
        },
        isStarted() {
          return this.state === 'started';
        },
        isActive() {
          return this.isOpened() || this.isStarted();
        },
        isSubmitting() {
          return this.state === 'submitting';
        },
        isSubmitted() {
          return this.state === 'submitted';
        },
        isTerminated() {
          return this.state === 'terminated';
        },
        isInProgress() {
          return !this.isSubmitted() && !this.isTerminated();
        },
        isClosed() {
          return (
            this.isSubmitting() || this.isSubmitted() || this.isTerminated()
          );
        },
        candidateSubmitted() {
          return this.closedReason === 'candidate_submitted';
        },
        timerRanOut() {
          return this.closedReason === 'timer_ran_out';
        },
        closedReasonText() {
          return CLOSED_REASONS[camelCase(this.closedReason)] || '';
        },
        cutOffTime() {
          if (this.timeLimit() && this.startedAt) {
            if (!isDate(this.startedAt)) {
              $log.error(`this.startedAt is not a date`, this.startedAt);
              return;
            }
            const milliseconds = this.timeLimitSeconds() * 1000;
            return new Date(this.startedAt.getTime() + milliseconds);
          }
        },
        retakeTimeLimitChange() {
          return (
            this.enforcedTimeLimit !== this.retrySchedule.enforcedTimeLimit
          );
        },
        timeLimit() {
          return this.assessment && this.assessment.enforcedTimeLimit;
        },
        timeLimitSeconds() {
          const limit = this.timeLimit();
          return limit && limit * 60;
        },
        lastHeartbeat() {
          const solutions = this.solutions;
          if (solutions && solutions.length) {
            return solutions
              .map((x) => x.lastHeartbeat)
              .sort(DateUtils.compareDate)[0];
          }
        },
        isAbandoned() {
          const tc = this.teamCandidate;
          const isPreview = tc && tc.testTeamMember;
          return this.abandoned && this.isStarted() && !isPreview;
        },
        detailedState() {
          let state = this.state;
          if (this.isSubmitted() && this.closedReason) {
            state += '-' + camelCase(this.closedReason);
          }
          return state;
        },
        stateName() {
          let name =
            DS.definitions.TeamCandidate.stateNameMappings[
              this.detailedState()
            ];
          if (this.isInRetryAccessWindow()) {
            name += ` (${this.isRetakeScheduled() ? 'Retake' : 'Reopen'} is scheduled)`;
          }
          return name;
        },
        retryState() {
          if (this.isReopenScheduled()) {
            return 'state-candidate-reopen-scheduled';
          } else if (this.isRetakeScheduled()) {
            return 'state-candidate-retake-scheduled';
          } else {
            return '';
          }
        },
        percentile(type) {
          return PercentileUtils.getPercentile(
            this[type],
            this.assessment && this.assessment[type + 'Stats'],
          );
        },
        hasStats(type) {
          return !!this.assessment && this.assessment.hasStats(type);
        },
        hasSubmittedReviews() {
          return (
            (this.assessmentResultReviews || []).filter(
              (r) => r.state === 'submitted',
            ).length > 0
          );
        },
        // when a blind reviewer has access to all reviews, we should still hide them when they haven't actually submitted thier own.
        shouldHideOtherReviews() {
          const teamMember = currentUser();
          return (
            teamMember &&
            teamMember.isBlindReviewer() &&
            (!this.myReview() || this.myReview().state !== 'submitted')
          );
        },
        blindedAssessmentResultReviews() {
          if (this.shouldHideOtherReviews()) {
            return this.assessmentResultReviews.filter(
              (arr) => arr.reviewedById === currentUser().id,
            );
          } else {
            return this.assessmentResultReviews;
          }
        },
        myReview() {
          if (!this._myReview) {
            const teamMember = currentUser();
            if (teamMember) {
              const arr = this.assessmentResultReviews.find(
                (arr) => arr.reviewedById === teamMember.id,
              );
              if (arr) {
                defineValue(this, '_myReview', arr);
              }
            }
          }
          return this._myReview;
        },

        /*
         * if there are result specific challenge items, return those, otherwise return
         * the items on the assessment itself.
         * The reason that challenge items sometimes exist on the result is that they act as a snapshot
         * of the assessment's state at the time of submission. This allows an assessment to be modified
         * without it affecting past submissions.
         */
        getChallengeItems() {
          // use the local challenge items if there are any
          if (this.challengeItems && this.challengeItems.length) {
            return this.challengeItems;
          } else {
            return this.assessment ? this.assessment.challengeItems : [];
          }
        },

        getChallengeItem(challenge) {
          return this.getChallengeItems().find(
            (ci) => ci.challengeId === challenge.id,
          );
        },

        /**
         * Returns true if this assessment result's snapshot is current with it's assessment
         * @returns {boolean}
         */
        isSnapshotCurrent() {
          const ar = this;
          const assessment = ar.assessment;
          const arChallengeItems = ar.challengeItems;
          const aChallengeItems = assessment && assessment.challengeItems;
          return (
            !arChallengeItems ||
            !aChallengeItems ||
            (aChallengeItems.length === arChallengeItems.length &&
              aChallengeItems.every((ci) => {
                const ciCopy = arChallengeItems.find(
                  (cic) => cic.challengeId === ci.challengeId,
                );
                return (
                  ciCopy &&
                  ci.position === ciCopy.position &&
                  ci.disabledLanguages.sort().join() ===
                    ciCopy.disabledLanguages.sort().join()
                );
              }))
          );
        },

        /**
         * Returns the solutions attached to this assessment result that have a corresponding
         * valid challenge item. This handles the case where the challenge items snapshot was reset
         * and we now have solutions pointing to an older assessment result
         * @returns {Array.<Solution>}
         */
        validSolutions() {
          let challengeItems = this.getChallengeItems();
          return (this.solutions || []).filter((sol) => {
            return challengeItems.some(
              (ci) => ci.challengeId === sol.challengeId,
            );
          });
        },
        trackProperties: function () {
          return {
            candidate: this.teamCandidate ? this.teamCandidate.name : undefined,
            candidateId: this.candidateId,
            assessment: this.assessment ? this.assessment.title : undefined,
            assessmentId: this.assessmentId,
            teamCandidateId: this.teamCandidateId,
            teamId: this.teamId,
          };
        },

        hasPostAssessmentActions() {
          return !!this.submissionMessage || this.hasPostAssessmentRedirect();
        },

        hasPostAssessmentRedirect() {
          return !!(
            this.$redirectUrl ||
            this.nextAssessmentId ||
            this.retrySchedule
          );
        },
        candidateLink() {
          if (this.invitation) {
            return this.invitation.inviteLink();
          } else if (this.assessment) {
            return this.assessment.assessUrl();
          }
        },
        reviewerLink() {
          return absoluteUrl(
            $state.href('liveInterview', { assessmentResultId: this.id }),
          );
        },
        reopenedByName() {
          if (this.reopenedBy) {
            return this.reopenedBy.name;
          } else if (
            this.teamCandidate &&
            this.teamCandidate.candidateId === this.reopenedById
          ) {
            return this.teamCandidate.nameOrEmail || 'Candidate';
          } else {
            return '(Missing Contact Info)';
          }
        },
      },

      relations: {
        hasOne: {
          Team: {
            localField: 'team',
            localKey: 'teamId',
          },
          AssessmentInvitation: {
            localField: 'invitation',
            localKey: 'assessmentResultId',
            get(AssessmentResult, relationDef, instance) {
              return instance.invitations.sort((a, b) =>
                DateUtils.compareDate(a.createdAt, b.createdAt),
              )[0];
            },
          },
        },
        hasMany: {
          Solution: {
            localField: 'solutions',
            foreignKey: 'assessmentResultId',
          },
          ChallengeItem: {
            localField: 'challengeItems',
            link: false,
          },
          TeamMember: {
            localField: 'interviewers',
            localKeys: 'interviewerIds',
          },
          AssessmentResultReview: {
            localField: 'assessmentResultReviews',
            foreignKey: 'assessmentResultId',
          },
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'assessmentResultId',
          },
          AssessmentInvitation: {
            localField: 'invitations',
            foreignKey: 'assessmentResultId',
          },
        },
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId',
          },
          Candidate: {
            localField: 'candidate',
            localKey: 'candidateId',
          },
          TeamCandidate: {
            localField: 'teamCandidate',
            localKey: 'teamCandidateId',
          },
          TeamMember: [
            {
              localField: 'reopenedBy',
              localKey: 'reopenedById',
            },
            {
              localField: 'accessUnlockedBy',
              localKey: 'accessUnlockedById',
            },
          ],
        },
      },

      meta: {
        fetchOnPush: (data) => {
          // only fetch result if we have the team candidate or candidate
          return (
            DS.get('TeamCandidate', data.teamCandidateId || '') ||
            DS.get('Candidate', data.candidateId || '')
          );
        },
        includeOnPull: ['signals_profile'],
        saveExcludes: [
          'assessment',
          'team',
          'solutions',
          'candidate',
          'teamCandidate',
          'challengeItems',
        ],
        actionEndpoints: {
          open: { method: 'PUT', track: true },
          unlockAccess: { method: 'PUT', track: true },
          start: { method: 'PUT', track: true },
          submit: { method: 'PUT', track: true },
          close: { method: 'PUT', track: true },
          terminate: { method: 'PUT', track: true },
          scheduleRetry: { method: 'PUT', track: true },
          cancelRetry: { method: 'PUT', track: true },
          restoreSubmit: { method: 'PUT', track: true },
          reset: { method: 'PUT', track: true },
          joinedInterview: { method: 'PUT', track: true },
          leftInterview: { method: 'PUT', track: true },
        },
        track: {
          create: true,
        },
      },
    });

    return AssessmentResult;
  });
